
// -----------------------------------------------------------------------------
// src/features/transactions/transactionSlice.js
// -----------------------------------------------------------------------------
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchTransactionsThunk,
  updateTransactionThunk,
  fetchSingleTransactionThunk,
  fetchAlertTransactionsThunk,
  fetchAlertFilterOptionsThunk,
} from './transactionThunks';

const initialState = {
  transactions: [],
  selectedTransaction: null,
  loading: false,
  singleLoading: false,
  error: null,
  next: null,
  count: 0,
  storeFilter: null,

  // Suspicious Transaction Alerts page (server-side filtered list)
  alerts: {
    items: [],
    count: 0,
    next: null,
    loading: false,
    loadingMore: false,
    error: null,
    requestId: null,
  },
  alertFilterOptions: {
    stores: [],
    devices: [],
    loading: false,
    error: null,
    requestId: null,
  },
};

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    resetTransactions(state) {
      state.transactions = [];
      state.next = null;
      state.count = 0;
      state.error = null;
      state.storeFilter = null;
    },
    resetSelectedTransaction(state) {
      state.selectedTransaction = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // alerts page list — only the latest request may write to state,
      // so fast filter changes can't show results for old filters
      .addCase(fetchAlertTransactionsThunk.pending, (state, action) => {
        const append = (action.meta.arg?.offset ?? 0) > 0;
        state.alerts.requestId = action.meta.requestId;
        state.alerts.error = null;
        if (append) state.alerts.loadingMore = true;
        else state.alerts.loading = true;
      })
      .addCase(fetchAlertTransactionsThunk.fulfilled, (state, action) => {
        if (state.alerts.requestId !== action.meta.requestId) return;
        const { results = [], next = null, count = 0 } = action.payload || {};
        const append = (action.meta.arg?.offset ?? 0) > 0;
        if (append) {
          const seen = new Set(state.alerts.items.map((t) => t.id));
          state.alerts.items.push(...results.filter((t) => !seen.has(t.id)));
        } else {
          state.alerts.items = results;
        }
        state.alerts.next = next;
        state.alerts.count = count;
        state.alerts.loading = false;
        state.alerts.loadingMore = false;
      })
      .addCase(fetchAlertTransactionsThunk.rejected, (state, action) => {
        if (state.alerts.requestId !== action.meta.requestId) return;
        state.alerts.loading = false;
        state.alerts.loadingMore = false;
        if (!action.meta.aborted) state.alerts.error = action.payload || 'Could not load transactions.';
      })

      // alerts page dropdown data
      .addCase(fetchAlertFilterOptionsThunk.pending, (state, action) => {
        state.alertFilterOptions.requestId = action.meta.requestId;
        state.alertFilterOptions.loading = true;
        state.alertFilterOptions.error = null;
      })
      .addCase(fetchAlertFilterOptionsThunk.fulfilled, (state, action) => {
        if (state.alertFilterOptions.requestId !== action.meta.requestId) return;
        state.alertFilterOptions.stores = action.payload?.stores || [];
        state.alertFilterOptions.devices = action.payload?.devices || [];
        state.alertFilterOptions.loading = false;
      })
      .addCase(fetchAlertFilterOptionsThunk.rejected, (state, action) => {
        if (state.alertFilterOptions.requestId !== action.meta.requestId) return;
        state.alertFilterOptions.loading = false;
        if (!action.meta.aborted) state.alertFilterOptions.error = action.payload;
      })

      // fetchAll
      .addCase(fetchTransactionsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { results, next, count } = action.payload;
        const offset = action.meta.arg?.offset ?? 0;
        if (offset > 0) {
          // infinite‑scroll – append
          state.transactions.push(...results);
        } else {
          // fresh load – replace
          state.transactions = results;
        }
        state.next = next;
        state.count = count;
        // Use store_id if provided, fall back to storeId for backward compatibility
        state.storeFilter = action.meta.arg?.store_id || action.meta.arg?.storeId || null;
      })
      .addCase(fetchTransactionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchSingle
      .addCase(fetchSingleTransactionThunk.pending, (state) => {
        state.singleLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleTransactionThunk.fulfilled, (state, action) => {
        state.singleLoading = false;
        state.selectedTransaction = action.payload;
        
        // Update in the list if it exists there
        const idx = state.transactions.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) {
          state.transactions[idx] = action.payload;
        }
      })
      .addCase(fetchSingleTransactionThunk.rejected, (state, action) => {
        state.singleLoading = false;
        state.error = action.payload;
      })
      
      // update
      .addCase(updateTransactionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        
        // Update in the main list if it exists there
        const idx = state.transactions.findIndex((t) => t.id === updated.id);
        if (idx !== -1) {
          state.transactions[idx] = updated;
        }
        
        const alertIdx = state.alerts.items.findIndex((t) => t.id === updated.id);
        if (alertIdx !== -1) {
          state.alerts.items[alertIdx] = updated;
        }

        // Update the selected transaction if it's the one that was updated
        if (state.selectedTransaction && state.selectedTransaction.id === updated.id) {
          state.selectedTransaction = updated;
        }
      })
      .addCase(updateTransactionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetTransactions, resetSelectedTransaction } = transactionSlice.actions;
export default transactionSlice.reducer;